import { cn } from "@core/utils/cn";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type TooltipProps = {
  content: string;
  children: ReactNode;
  className?: string;
};

type Coords = { top: number; left: number };

// Gap (px) between the anchor and the tooltip bubble.
const OFFSET = 10;

export function Tooltip({ content, children, className }: TooltipProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const show = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Position is fixed to the viewport so the bubble escapes any ancestor
    // with `overflow: hidden/auto` (the tree's collapse animation + scroll
    // container would otherwise clip it). The bubble grows upward from this
    // point via `translateY(-100%)`.
    setCoords({ top: rect.top - OFFSET, left: rect.left });
  }, []);

  const hide = useCallback(() => setCoords(null), []);

  if (!content) return <>{children}</>;

  return (
    <div
      ref={wrapperRef}
      className={cn("relative min-w-0 w-full", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {coords &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform: "translateY(-100%)",
            }}
            className={cn(
              // layout
              "z-[9999] pointer-events-none w-max max-w-[13rem] break-words",
              // appearance
              "rounded-lg bg-neutral-900 px-3 py-2",
              "text-xs font-medium leading-snug text-white shadow-lg",
              // arrow (downward triangle anchored left, points at the anchor)
              "after:absolute after:top-full after:left-4",
              "after:h-0 after:w-0 after:content-['']",
              "after:border-l-[5px] after:border-l-transparent",
              "after:border-r-[5px] after:border-r-transparent",
              "after:border-t-[6px] after:border-t-neutral-900",
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default Tooltip;
